// ---------------------------------------------------------------------------
// Completion-time consumption preview ("Stock from CAD", spec §7): stock
// already depletes by time (the inventory level's daily rate), so completing
// a task should only deduct what time did NOT already account for. This
// mirrors the backend `planConsumption` (`backend/src/core/inventory/
// consumption.ts`) formula exactly — deduct = max(0, claimed − dailyRate ×
// actualDays) — so the Overview tab's pre-click preview always agrees with
// what the server will actually deduct on Complete.
//
// Pure functions — no React, no store; unit-tested in isolation.
// ---------------------------------------------------------------------------

import type { ITaskLogEntry, ISchedulerTask, ITaskStockClaim } from '@interfaces/IScheduler';
import type { InventoryLevel } from '@interfaces/ILogistics';
import type { ITaskStock } from './useTaskStock';
import { fold } from '../../LogisticsPage/units';

export interface IConsumptionPreviewItem {
  materialName: string;
  unit: string;
  consumedByTime: number;
  deduct: number;
}

const round1 = (n: number): number => Math.round(n * 10) / 10;

/** The calendar day (YYYY-MM-DD) an instant falls on, in `tz`. */
function dayInZone(iso: string, tz: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(new Date(iso));
}

/** Whole calendar days between two instants, counted in `tz`. Mirrors the
 *  backend's `daysBetweenInZone` (`core/inventory/derive.ts`) — the frontend
 *  has no import path into backend code, so the day-count crosses a tz
 *  boundary exactly once, here, rather than being re-derived at every call
 *  site that needs it. */
function daysBetweenInZone(fromIso: string, toIso: string, tz: string): number {
  return Math.round((Date.parse(dayInZone(toIso, tz)) - Date.parse(dayInZone(fromIso, tz))) / 86_400_000);
}

/**
 * The instant a task was actually started: the `at` of the first `progress`
 * log entry that moved it off zero without finishing it — since the 2026-09-17
 * task-commit design there is no `in-progress` STATUS to key off, so the first
 * report of work IS the start. Falls back to the plan's `start` date at
 * midnight UTC when nothing was ever reported. Mirrors the backend
 * `stockCompletionInput`'s `actualStart` resolution 1:1 so the frontend
 * preview and the server's own reconciliation never disagree on which day
 * counting began.
 */
export function actualStartIso(task: Pick<ISchedulerTask, 'start' | 'log'>): string {
  const firstStart = (task.log ?? []).find(
    (e): e is Extract<ITaskLogEntry, { kind: 'progress' }> =>
      e.kind === 'progress' && e.data.from === 0 && e.data.to > 0 && e.data.to < 100,
  );
  return firstStart ? firstStart.at : `${task.start}T00:00:00Z`;
}

/** Whole days elapsed since the task actually started, floored at 1 (spec
 *  §7's `actualDays`) — exposed separately from `previewConsumption` so the
 *  Overview tab's "day {{day}} of {{planned}}" helper text can show it
 *  without re-deriving the day-diff logic itself. */
export function actualDaysElapsed(task: ISchedulerTask, todayIso: string, tz: string): number {
  return Math.max(1, daysBetweenInZone(actualStartIso(task), todayIso, tz));
}

/** The task's originally planned span in whole days, inclusive of both ends
 *  (`start`/`end` are both inclusive calendar dates) — the "of {{planned}}"
 *  half of the Overview helper text. Bare `YYYY-MM-DD` dates carry no tz. */
export function plannedDays(task: Pick<ISchedulerTask, 'start' | 'end'>): number {
  return Math.round((Date.parse(task.end) - Date.parse(task.start)) / 86_400_000) + 1;
}

/**
 * Previews, per claimed material, how much would actually be deducted from
 * stock if the task completed right now. A claim whose material has no
 * matching inventory level yields no item (nothing to preview against) —
 * this mirrors the backend's `'no-stock'` skip, which deducts nothing either.
 */
export function previewConsumption(
  task: ISchedulerTask,
  inventory: InventoryLevel[],
  todayIso: string,
  tz: string,
): IConsumptionPreviewItem[] {
  const actualDays = actualDaysElapsed(task, todayIso, tz);
  const items: IConsumptionPreviewItem[] = [];
  for (const claim of task.stockClaims ?? []) {
    const level = inventory.find((l) => fold(l.materialName) === fold(claim.materialName));
    if (!level) continue;
    const consumedByTime = round1(level.dailyRate * actualDays);
    const deduct = Math.max(0, round1(claim.quantity - consumedByTime));
    items.push({ materialName: level.materialName, unit: claim.unit, consumedByTime, deduct });
  }
  return items;
}

/**
 * Every claim on `task` whose evaluation reports a CAD-version drift (spec
 * §8: a claim that exceeded the ceiling after a CAD version change) — the set
 * that drives the amber flag on the Gantt row.
 */
export function claimsExceedingCad(
  task: ISchedulerTask,
  stock: Pick<ITaskStock, 'evaluate'>,
): ITaskStockClaim[] {
  return (task.stockClaims ?? []).filter((claim) => stock.evaluate(claim).cadChanged !== null);
}
